// import hoja de estilos (css)
import "./ComportamientoGrafica.css";

// import hooks
import { useContext } from "react";

// import context
import { DarkModeContext } from "../../context/DarkModeContext";

// import librerias
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import {
  faCircleUp,
  faCircleArrowDown,
  faCirclePause,
} from "@fortawesome/free-solid-svg-icons";
import { Line } from "react-chartjs-2";

// import helpers
import convertirDecimalAPorcentaje from "../../helpers/convertirPorcentajeADecimal";
import { formatearAPeso } from "../../helpers/formatearAPeso";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const salesLineColors = ["#b4c400", "#9d9d9d", "#292b2f", "#6f747d", "#35c36b"];
const diasBaseGrafico = ["lunes", "martes", "mi\u00e9rcoles", "jueves", "viernes"];

const ComportamientoGrafica = ({ datos, secondaryContent = null }) => {
  // context para el modo claro/oscuro
  const { darkMode } = useContext(DarkModeContext);
  // opcion de color
  const tickColor = darkMode ? "#c9ced6" : "#a5abb4";
  const gridColor = darkMode ? "rgba(255, 255, 255, 0.1)" : "#e3e6ea";

  // extraigo los datos
  const {
    comparativahoy,
    comparativaHoymesanterior,
    totalesPorDiaTarjeta,
    porcentaje,
  } = datos;

  // datos de mes actual y mes anterior
  const { mesActual, mesAnterior } = datos.comparativaMes || {};

  // variables totales
  const totales = totalesPorDiaTarjeta || [];
  const tiendas = Object.keys(totales);

  // Crear un array para almacenar los dias
  let diasArray = [];
  // Iterar sobre cada tienda
  for (let tienda in totales) {
    if (totales.hasOwnProperty(tienda)) {
      // Obtener los dias de esa tienda y anadirlos al array
      diasArray = diasArray.concat(totales[tienda].map((valor) => valor.key));
    }
  }
  // Si quieres eliminar duplicados, convierte el array en un Set y luego de nuevo en un array
  diasArray = [...new Set(diasArray)];

  function ordenarDias(dia) {
    const orden = {
      lunes: 1,
      martes: 2,
      miercoles: 3,
      ["mi\u00e9rcoles"]: 3,
      jueves: 4,
      viernes: 5,
      sabado: 6,
      ["s\u00e1bado"]: 6,
      domingo: 7,
    };
    return orden[dia] || 8;
  }

  diasArray.sort((a, b) => ordenarDias(a) - ordenarDias(b));

  const normalizarDia = (dia) =>
    (dia || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const diasGrafico = [
    ...diasArray.slice(0, 5),
    ...diasBaseGrafico.filter(
      (dia) =>
        !diasArray.some(
          (diaExistente) => normalizarDia(diaExistente) === normalizarDia(dia)
        )
    ),
  ].slice(0, 5);

  const datasets = tiendas.map((tienda, index) => {
    const valoresTienda = totales[tienda];
    const valoresPorDia = valoresTienda.reduce((acumulador, valor) => {
      acumulador[normalizarDia(valor.key)] = valor.value;
      return acumulador;
    }, {});
    let ultimoValorDisponible = null;
    const totalConDescuentoPorDias = diasGrafico.map((dia) => {
      const claveDia = normalizarDia(dia);
      if (Object.prototype.hasOwnProperty.call(valoresPorDia, claveDia)) {
        ultimoValorDisponible = valoresPorDia[claveDia];
        return valoresPorDia[claveDia];
      }
      return ultimoValorDisponible;
    });
    const color = salesLineColors[index % salesLineColors.length];

    return {
      label: tienda,
      borderColor: color,
      backgroundColor: color,
      data: totalConDescuentoPorDias,
      borderWidth: 4,
      pointRadius: 0,
      pointHoverRadius: 6,
      pointBackgroundColor: color,
      pointBorderColor: color,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: "#ffffff",
      pointHoverBorderWidth: 2,
      tension: 0.42,
      fill: false,
      spanGaps: true,
    };
  });

  const commonTooltipOptions = {
    enabled: true,
    mode: "index",
    intersect: false,
    bodyFont: {
      size: 12,
    },
    padding: 10,
    boxWidth: 6,
    boxHeight: 6,
  };

  const options = {
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    elements: {
      line: {
        capBezierPoints: true,
      },
    },
    plugins: {
      legend: {
        labels: {
          boxWidth: 18,
          boxHeight: 5,
          usePointStyle: false,
          useBorderRadius: true,
          borderRadius: 8,
          color: darkMode ? "#d7dbe2" : "#6f747d",
          padding: 20,
          font: {
            size: 11,
            family: "lato-Regular, Arial, sans-serif",
          },
        },
        display: false,
        position: "top",
        align: "start",
      },
      tooltip: {
        ...commonTooltipOptions,
        backgroundColor: "#292b2f",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#292b2f",
        borderWidth: 0,
        cornerRadius: 8,
        displayColors: true,
        boxPadding: 5,
        callbacks: {
          title: function () {
            return "";
          },
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += " ";
            }
            label += formatearAPeso(context.parsed.y || 0);
            return label;
          },
        },
      },
    },
    responsive: true,

    scales: {
      y: {
        display: true,
        grid: {
          display: true,
          color: gridColor,
          drawBorder: false,
        },
        ticks: {
          color: tickColor,
          count: 4,
          maxTicksLimit: 4,
          padding: 10,
          callback: function (value) {
            const absoluteValue = Math.abs(Number(value) || 0);
            if (absoluteValue >= 1000000) {
              return `$${(absoluteValue / 1000000).toFixed(1)} M`;
            }
            if (absoluteValue >= 1000) {
              return `$${Math.round(absoluteValue / 1000)} mil`;
            }
            return formatearAPeso(value).replace(",00", "");
          },
          font: {
            size: 11,
            family: "lato-Regular, Arial, sans-serif",
          },
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: tickColor,
          padding: 10,
          font: {
            size: 11,
            family: "lato-Regular, Arial, sans-serif",
          },
        },
      },
    },
  };

  const data = {
    labels: diasGrafico,
    datasets: datasets,
  };

  let numeroPorcentual = porcentaje || 0;
  let porcentajeFinal = numeroPorcentual.toFixed(2);
  let porcentajeNumero = porcentajeFinal;

  let porcentajeEnteroComparativaDiaMes =
    convertirDecimalAPorcentaje(porcentajeNumero);

  const valorMesAnterior = Number(comparativaHoymesanterior) || 0;
  const valorMesActual = Number(comparativahoy) || 0;
  const valorMaximoComparativa = Math.max(
    Math.abs(valorMesAnterior),
    Math.abs(valorMesActual),
    1
  );
  const diferenciaComparativa = valorMesAnterior - valorMesActual;
  const comparativaResumen = [
    {
      label: mesAnterior || "Mes anterior",
      value: valorMesAnterior,
      type: "previous",
    },
    {
      label: mesActual || "Mes actual",
      value: valorMesActual,
      type: "current",
    },
  ];
  const variationTrend =
    Number(porcentajeNumero) > 0
      ? "is-up"
      : Number(porcentajeNumero) < 0
      ? "is-down"
      : "is-flat";

  const mostrarIcono = () => {
    if (porcentajeNumero > 0) {
      return (
        <div>
          <FontAwesomeIcon
            className="color-verde me-2 fs-25"
            icon={faCircleUp}
          />
        </div>
      );
    } else if (porcentajeNumero === 0) {
      return (
        <div>
          <FontAwesomeIcon
            className="color-negro me-2 fs-25 fa-rotate-90"
            icon={faCirclePause}
          />
        </div>
      );
    } else {
      return (
        <div>
          <FontAwesomeIcon
            className="color-rojo me-2 fs-25"
            icon={faCircleArrowDown}
          />
        </div>
      );
    }
  };

  const ventasLegend = (
    <div className="aliado-sales-legend" aria-hidden="true">
      {tiendas.map((tienda, index) => (
        <span key={tienda}>
          <i style={{ backgroundColor: salesLineColors[index % salesLineColors.length] }} />
          {tienda}
        </span>
      ))}
    </div>
  );

  const comparativaMesAnterior = (
    <div
      className={
        darkMode
          ? "bg-grafica-dark aliado-comparison-card aliado-comparison-summary-card"
          : "bg-grafica aliado-comparison-card aliado-comparison-summary-card"
      }
    >
      <h2>Comparativa mes anterior</h2>
      <div className="aliado-comparison-bars">
        {comparativaResumen.map((item) => {
          const barWidth = Math.max(
            4,
            (Math.abs(item.value) / valorMaximoComparativa) * 100
          );

          return (
            <div className="aliado-comparison-bar-row" key={item.type}>
              <span className="aliado-comparison-label">{item.label}</span>
              <div className="aliado-comparison-bar-line">
                <span
                  className={
                    item.type === "current"
                      ? "aliado-comparison-bar is-current"
                      : "aliado-comparison-bar is-previous"
                  }
                  style={{ width: `${barWidth}%` }}
                />
              </div>
              <strong>{formatearAPeso(item.value)}</strong>
            </div>
          );
        })}
      </div>
      <div className="aliado-comparison-totals">
        <div>
          <strong>{formatearAPeso(valorMesActual)}</strong>
          <span>Ventas netas</span>
        </div>
        <div className="aliado-comparison-variation">
          <div className={`aliado-variation-pill ${variationTrend}`}>
            {mostrarIcono()}
            <span>{porcentajeEnteroComparativaDiaMes} %</span>
          </div>
          <span>Variacion</span>
        </div>
        <div>
          <strong>{formatearAPeso(Math.abs(diferenciaComparativa))}</strong>
          <span>Diferencia</span>
        </div>
      </div>
    </div>
  );

  return (
    <section translate="no">
      <article className="container d-none d-md-block">
        <div className="row aliado-top-chart-row">
          <div className="col-12 aliado-top-chart-col">
            <div
              className={
                darkMode
                  ? "bg-grafica-dark aliado-sales-card"
                  : "bg-grafica aliado-sales-card"
              }
            >
              <div className="aliado-sales-card-header">
                <h2>Evolucion de ventas</h2>
                <div className="aliado-sales-controls" aria-hidden="true">
                  <span>Montos</span>
                  <span>Todas las terminales</span>
                </div>
              </div>
              {ventasLegend}
              <div className="d-flex justify-content-center aliado-sales-chart">
                <Line className="px-3" data={data} options={options} />
              </div>
            </div>
          </div>
          <div className="col-12 aliado-top-chart-col">
            <div className="d-flex justify-content-center">
              {secondaryContent || comparativaMesAnterior}
            </div>
          </div>
        </div>
        {secondaryContent ? (
          <div className="row aliado-comparison-row">
            <div className="col-12">{comparativaMesAnterior}</div>
          </div>
        ) : null}
      </article>
      <article className="mt-3 d-md-none d-block container">
        <div className="row">
          <div className="my-2">
            <div className="d-flex justify-content-center ">
              <div
                className={
                  darkMode
                    ? "bg-comportamiento-dark aliado-sales-card"
                    : "bg-comportamiento aliado-sales-card"
                }
              >
                <div className="aliado-sales-card-header">
                  <h2>Evolucion de ventas</h2>
                  <div className="aliado-sales-controls" aria-hidden="true">
                    <span>Montos</span>
                    <span>Todas las terminales</span>
                  </div>
                </div>
                {ventasLegend}
                <div className="d-flex justify-content-center aliado-sales-chart">
                  <Line
                    style={{ width: "100%" }}
                    data={data}
                    options={options}
                  />
                </div>
              </div>
            </div>
          </div>
          {secondaryContent ? (
            <div className="my-2">
              <div className="d-flex justify-content-center">
                {secondaryContent}
              </div>
            </div>
          ) : null}
          <div className="my-2 ">
            <div className="d-flex justify-content-center ">
              {comparativaMesAnterior}
            </div>
          </div>
        </div>
      </article>
    </section>
  );
};

export default ComportamientoGrafica;
